/**
 * TagIconPicker — grid de emojis curados pra ícone de tag (BUG-V2-054).
 *
 * Substitui input text livre. Memória `feedback_design_system_rich_editor`
 * estabeleceu regra inviolável: emoji sempre via picker visual, nunca texto
 * digitado.
 *
 * Decisão arquitetural: NÃO usar `emoji-mart` ou `emoji-picker-react` (libs
 * ~50KB+) — pra tags, conjunto curado de ~40 emojis cobre os casos
 * canônicos (status, prioridade, áreas, tipos). Anti-bloat conforme
 * restrição da rodada.
 *
 * Compat retroativa: campo aceita string emoji que NÃO está na lista
 * curada — `value` é renderizado tal qual. Botão "limpar" devolve null.
 * Tags antigas com emojis customizados (ex: 🐛, 🦄) continuam renderizando.
 */

import { useRef, useState } from 'react';

import { Popover } from '@/components/ui';
import { cn } from '@/lib/cn';

/**
 * Emojis curados pra tags — agrupados por categoria (não exposto na UI,
 * é só pra ordem). Inclui os mais úteis pra workflows IGM Security:
 * prioridade, status, tipo de serviço, alerta, área.
 */
const CURATED_EMOJIS: string[] = [
  // Prioridade / urgência
  '🔥', '⚡', '🚨', '⚠️', '⏰',
  // Status / progresso
  '✅', '❌', '⏳', '🔒', '🔓',
  // Destaque / atenção
  '⭐', '👀', '🎯', '📌', '🔔',
  // Tipos de tag comuns
  '💼', '🛠️', '🔧', '🏷️', '📋',
  // Áreas IGM Security
  '📹', '🚪', '🏢', '🚧', '📞',
  // Comercial
  '💰', '💎', '🤝', '📊', '📈',
  // Outros úteis
  '🎉', '🏆', '🐛', '🆕', '🔁',
  '✨', '💡', '🚀', '🌟', '📝',
];

interface TagIconPickerProps {
  /** Emoji atual (string, 1-4 chars) ou null/empty pra "sem ícone". */
  value: string;
  onChange: (icone: string) => void;
  className?: string;
}

export function TagIconPicker({ value, onChange, className = '' }: TagIconPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  function handlePick(emoji: string) {
    onChange(emoji);
    setOpen(false);
  }

  function handleClear() {
    onChange('');
    setOpen(false);
  }

  return (
    <div data-testid="tag-icon-picker" ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="tag-icon-picker-trigger"
        aria-label="Escolher ícone da tag"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-2 rounded-xl border border-line dark:border-line-dark',
          'bg-paper-surface dark:bg-paper-dark-surface px-3 py-2 text-sm',
          'hover:bg-paper-warm/40 dark:hover:bg-paper-dark-warm/40 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-red focus:border-red',
        )}
      >
        {value ? (
          <span className="text-lg leading-none" data-testid="tag-icon-picker-current">
            {value}
          </span>
        ) : (
          <span className="text-ink-3 dark:text-ink-dark-3 italic">Sem ícone</span>
        )}
        <span className="text-xs text-ink-3 dark:text-ink-dark-3">
          {open ? '▾' : '▸'}
        </span>
      </button>

      {/* CENSO-FLUTUANTE (05/08): era absolute (clipado no TagQuickAddDialog e
          na TagsCatalogPage em modal) — migrado pro <Popover> canônico. */}
      <Popover aberto={open} gatilhoRef={containerRef} onFechar={() => setOpen(false)}>
        <div
          data-testid="tag-icon-picker-popover"
          className={cn(
            'w-72',
            'rounded-xl border border-line dark:border-line-dark',
            'bg-paper-surface dark:bg-paper-dark-surface shadow-lg',
            'p-2',
          )}
          role="listbox"
          aria-label="Emojis disponíveis"
        >
          <div className="grid grid-cols-8 gap-1">
            {CURATED_EMOJIS.map((emoji) => {
              const selected = value === emoji;
              return (
                <button
                  key={emoji}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-testid={`tag-icon-option-${emoji}`}
                  onClick={() => handlePick(emoji)}
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-lg text-lg',
                    'hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-red',
                    selected && 'bg-red-soft/40 ring-2 ring-red',
                  )}
                  aria-label={`Escolher ${emoji}`}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
          {value && (
            <div className="mt-2 pt-2 border-t border-line/60 dark:border-line-dark/60 flex justify-between items-center">
              <span className="text-xs text-ink-3 dark:text-ink-dark-3 pl-2">
                Atual: <span className="text-base ml-1">{value}</span>
              </span>
              <button
                type="button"
                onClick={handleClear}
                data-testid="tag-icon-picker-clear"
                className="text-xs text-ink-3 hover:text-red px-2 py-1 transition-colors"
              >
                Limpar
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}
